"use client";

import Script from "next/script";
import Link from "next/link";
import ProductCarousel from "@/components/ProductCarousel";
import ProductInfo from "@/components/ProductInfo";
import PdfVersion from "@/components/PdfVersion";
import PdfDetailShell from "@/components/summary-pdf/PdfDetailShell";
import CompleteUpsellBanner from "@/components/summary-pdf/CompleteUpsellBanner";

const product = {
  id: "25-2-super-compressed-pdf",
  title: "[초압축]\n2025년 2회\n실기 이론 요약 PDF",
  description:
    "2025년 2회 실기 시험(이전 실기 시험)을 위한 초압축된 이론 요약 PDF입니다. 핵심 개념들을 간결하게 정리하여 효율적인 학습이 가능합니다.",
  price: 1000,
  orderIdPrefix: "order_",
  images: [
    { src: "/summary-pdf/25-2-super-compressed/1.webp", alt: "PDF 미리보기 1" },
    { src: "/summary-pdf/25-2-super-compressed/2.webp", alt: "PDF 미리보기 2" },
    { src: "/summary-pdf/25-2-super-compressed/3.webp", alt: "PDF 미리보기 3" },
    { src: "/summary-pdf/25-2-super-compressed/4.webp", alt: "PDF 미리보기 4" },
    { src: "/summary-pdf/25-2-super-compressed/5.webp", alt: "PDF 미리보기 5" },
  ],
};

export default function SuperCompressedPDFClient() {
  // 결제 박스(ProductInfo) — 셸이면 우측 sticky 슬롯, 폴백이면 캐러셀 옆. 한 번만 만들어 재사용
  // (결제 핸들러/폴링/모달/콜백/GSI 로직은 ProductInfo 내부 — 위치만 이동, 변경 없음).
  const productInfo = (
    <ProductInfo
      title={product.title}
      price={product.price}
      isYoutubeSubscriber={false}
      onYoutubeVerificationSuccess={() => {}}
      orderIdPrefix={product.orderIdPrefix}
      productId="25-2-super-compressed-pdf"
      nicepayClientId={process.env.NEXT_PUBLIC_NICEPAY_CLIENT_ID!}
      showYoutubeDiscount={false}
      youtubeChannelId={process.env.NEXT_PUBLIC_YOUTUBE_CHANNEL_ID}
      youtubeClientId={process.env.NEXT_PUBLIC_YOUTUBE_CLIENT_ID}
    />
  );

  // ── Complete 이용권 안내 배너(JCG-2060) ───────────────────────────────────
  // 풀폭(폴백 본문)/컴팩트(셸 좌측 사이드바)를 공유 컴포넌트(CompleteUpsellBanner)로
  // 통일. 5개 PDF 상세가 동일 소스를 소비한다(drift 방지). 셸=좌측 사이드바 / 폴백=본문 분리.
  const completeBannerCompact = <CompleteUpsellBanner variant="compact" />;
  const completeBanner = <CompleteUpsellBanner variant="full" />;

  // ── 코딩 요약 PDF 크로스셀 배너 ───────────────────────────────────────────
  // 이론 PDF 상세에서 코딩 요약 PDF 상품(/summary-pdf/26-1-coding-compressed)으로 유도.
  // 셸(데스크탑)에선 좌측 사이드바 컴팩트 버전, 폴백(모바일)에선 본문 풀폭 버전("셸=사이드바
  // / 폴백=본문" 분리). Complete(amber)와 구분되는 blue/indigo 액센트로, 컴팩트는 SectionLevel
  // blue 페어(dark-mode-colors.md §3-2), 풀폭 그라데이션은 cross-mode 예외(§2).
  const codingPdfBannerCompact = (
    <Link
      href="/summary-pdf/26-1-coding-compressed"
      className="block break-words rounded-xl border border-blue-200 bg-blue-50 p-4 transition-all hover:bg-blue-100 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-blue-900 dark:bg-blue-950/40 dark:hover:bg-blue-950/60"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-bold text-blue-800 dark:text-blue-300">
          💻 실기 코딩 요약 PDF
        </h2>
        <span aria-hidden="true" className="shrink-0 text-lg text-blue-600 dark:text-blue-400">
          &rarr;
        </span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-blue-700 dark:text-blue-200">
        C·Java·Python 기출 코드 패턴을 압축 정리한 2026년 1·2회 통합 실기 코딩 요약 PDF도 함께 학습하세요!
      </p>
    </Link>
  );

  const codingPdfBanner = (
    <Link
      href="/summary-pdf/26-1-coding-compressed"
      className="block bg-gradient-to-r from-indigo-500 to-blue-500 p-6 rounded-lg text-white hover:from-indigo-600 hover:to-blue-600 transition-all hover:shadow-lg"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold mb-1">💻 실기 코딩 요약 PDF</h3>
          <p className="text-sm text-blue-100">
            C·Java·Python 기출 코드 패턴을 압축 정리한 2026년 1·2회 통합 실기 코딩 요약 PDF도 함께 학습하세요!
          </p>
        </div>
        <div className="text-2xl ml-4" aria-hidden="true">
          &rarr;
        </div>
      </div>
    </Link>
  );

  // 좌측 사이드바 노드(셸 데스크탑 전용) — Complete 안내 + 코딩 PDF 크로스셀.
  const leftAside = (
    <div className="flex flex-col gap-4">
      {completeBannerCompact}
      {codingPdfBannerCompact}
    </div>
  );

  // 셸 가운데 본문 — 캐러셀 + 세일즈 상세. Complete 안내는 좌측 사이드바(leftAside)로 이동.
  // 섹션 앵커 네비 제거로 <section id>·scroll-mt 도 단순 div 로 정리.
  const shellBody = (
    <>
      <div className="mb-10">
        {/* 캐러셀 루트의 lg:w-2/3 를 중앙 컬럼에선 전체 폭으로 덮어쓴다. */}
        <div className="[&>div]:lg:w-full">
          <ProductCarousel images={product.images} />
        </div>
      </div>

      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-foreground">상품 설명</h2>
        <div className="prose max-w-none">
          <p className="text-muted-foreground leading-relaxed text-base">
            {product.description}
          </p>
        </div>
        <div className="mt-2">
          <PdfVersion id="25-2-super-compressed-pdf" defaultExpanded={true} />
        </div>
      </div>
    </>
  );

  // 셸 밖(모바일/태블릿/SSR) 폴백 — 기존 클래식 풀블리드 랜딩(레이아웃·결제 위치 byte-identical).
  const fallback = (
    <div className="flex flex-col w-screen relative left-1/2 transform -translate-x-1/2">
      <main className="flex-1 px-4 py-8">
        {/* 위쪽: 상품 정보 */}
        <div className="flex justify-center mb-12">
          <div className="w-full max-w-[1000px] px-4">
            <div className="flex flex-col lg:flex-row gap-2 lg:gap-15">
              {/* 상품 이미지 Carousel */}
              <ProductCarousel images={product.images} />

              {/* 상품 정보 및 결제 */}
              {productInfo}
            </div>
          </div>
        </div>

        {/* 아래쪽: 상품 설명 */}
        <div className="max-w-[700px] mx-auto px-4">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-foreground">상품 설명</h2>
            <div className="prose max-w-none">
              <p className="text-muted-foreground leading-relaxed text-base">
                {product.description}
              </p>
            </div>
            <div className="mt-2">
              <PdfVersion id="25-2-super-compressed-pdf" defaultExpanded={true} />
            </div>
            {/* 폴백(모바일)엔 좌측 사이드바가 없어 Complete 안내·코딩 크로스셀을 본문에 둔다. */}
            <div className="mt-6 space-y-6">
              {completeBanner}
              {codingPdfBanner}
            </div>
          </div>
        </div>
      </main>
    </div>
  );

  return (
    <>
      <Script src={process.env.NEXT_PUBLIC_NICEPAY_JS_SDK_URL}></Script>
      <Script src="https://accounts.google.com/gsi/client" />
      <PdfDetailShell leftAside={leftAside} cta={productInfo} fallback={fallback}>
        {shellBody}
      </PdfDetailShell>
    </>
  );
}
